import React, { useMemo } from 'react';
import { Box, Checkbox, Stack } from '@mui/material';
import { GenericBarChart } from '@/components/shared/GenericBarChart';
import { Can } from '@/components/Can';
import { scrollDesign } from '../../styles';
import { useFindingsData } from './hooks/useFindingsData';
import {
  DefectsAreaForCustomBlocksQuery,
  PointOfInterestsFindingsByDeckAndPaintBlock,
} from '@/__generated__/graphql';
import { CustomLegend } from './CustomLegend';
import { CheckboxLabel, PaintBlockCheckboxStyles } from './style';
import { titleCase } from '@/utils';

type Props = {
  selectedPOIType: string[];
  findingsByPaintBlocksAndDecks: PointOfInterestsFindingsByDeckAndPaintBlock | undefined;
  defectAreaForCustomBlocks:
    | DefectsAreaForCustomBlocksQuery['defectsAreaForAssemblyPaintRegionsByGroup']
    | undefined;
  graphsContainerWidth: number;
  activeStructureIds?: string[];
  changePOITypeTab: (argument0: string | null | undefined) => void;
};

export const FindingsGraphs = ({
  selectedPOIType,
  findingsByPaintBlocksAndDecks,
  graphsContainerWidth,
  changePOITypeTab,
  activeStructureIds = [],
}: Props) => {
  const { chartsData, findingsLegend, pointOfInterestTemplates } = useFindingsData(
    findingsByPaintBlocksAndDecks,
    selectedPOIType,
    activeStructureIds
  );

  const POISelections = useMemo(() => {
    return (
      <Can I="read" a="PointOfInterest">
        <Stack alignItems="center" direction="row" marginBottom={2.5} marginTop={0.5}>
          {pointOfInterestTemplates.map((template) => (
            <>
              {template && (
                <Stack key={template?.id} alignItems="center" direction="row" marginRight={1.5}>
                  <Checkbox
                    checked={selectedPOIType.includes(template.name.toLocaleLowerCase())}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => changePOITypeTab(template.name.toLowerCase())}
                    sx={PaintBlockCheckboxStyles}
                  />
                  <CheckboxLabel>{titleCase(template.name)}</CheckboxLabel>
                </Stack>
              )}
            </>
          ))}
        </Stack>
      </Can>
    );
  }, [pointOfInterestTemplates, selectedPOIType, changePOITypeTab]);

  return (
    <Can I="read" a="PointOfInterest">
      {POISelections}
      <Stack direction="column" gap={2} mb={8}>
        <Stack direction="row" spacing={2}>
          <Box sx={{ width: '30%', ...scrollDesign }} data-testid="counts-by-deck-chart">
            <GenericBarChart
              title="Finding count by deck"
              data={chartsData.findingsCountByDeck}
              legendData={findingsLegend}
              containerWidth={graphsContainerWidth}
              removeZeroValueRecords
              hideLegend
              showBarOutline
            />
          </Box>
          <Box sx={{ width: '70%', ...scrollDesign }} data-testid="counts-by-block-chart">
            <GenericBarChart
              title="Finding count by paint block"
              data={chartsData.findingsCountByPaintBlock}
              legendData={findingsLegend}
              showRecordsLimit
              containerWidth={graphsContainerWidth}
              removeZeroValueRecords
              hideLegend
              showBarOutline
            />
          </Box>
        </Stack>
        <CustomLegend data={findingsLegend} />
      </Stack>
    </Can>
  );
};
